import React, { useEffect, useState } from 'react';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { adminPopupAnnouncementService } from '../services';
import { notifyError, notifySuccess } from '../utils/toast';
import { resolveMediaUrl } from '../services/api';

const defaultForm = {
    title: '',
    message: '',
    type: 'announcement',
    status: 'active',
    image: null,
    rating: 5,
    donation_goal: '',
    donation_raised: '',
    start_at: '',
    end_at: '',
    show_once: true,
    priority: 0,
};

const PopupAnnouncements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(defaultForm);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const response = await adminPopupAnnouncementService.getAll();
            setAnnouncements(response.data.data || []);
        } catch (err) {
            notifyError('Unable to load popup announcements.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] || null : value,
        }));
    };

    const resetForm = () => {
        setFormData(defaultForm);
        setShowForm(false);
        setEditingId(null);
        setError('');
    };

    const handleEdit = (item) => {
        setFormData({
            title: item.title || '',
            message: item.message || '',
            type: item.type || 'announcement',
            status: item.status || 'active',
            image: null,
            rating: item.rating || 5,
            donation_goal: item.donation_goal || '',
            donation_raised: item.donation_raised || '',
            start_at: item.start_at ? item.start_at.slice(0, 16) : '',
            end_at: item.end_at ? item.end_at.slice(0, 16) : '',
            show_once: !!item.show_once,
            priority: item.priority || 0,
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const normalizePayload = (payload) => ({
        ...payload,
        start_at: payload.start_at || null,
        end_at: payload.end_at || null,
        priority: Number(payload.priority || 0),
        rating: Number(payload.rating || 0) || null,
        donation_goal: payload.donation_goal === '' ? null : Number(payload.donation_goal),
        donation_raised: payload.donation_raised === '' ? null : Number(payload.donation_raised),
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const payload = normalizePayload(formData);
            if (editingId) {
                await adminPopupAnnouncementService.update(editingId, payload);
                notifySuccess('Popup announcement updated.');
            } else {
                await adminPopupAnnouncementService.create(payload);
                notifySuccess('Popup announcement created.');
            }
            await fetchAnnouncements();
            resetForm();
        } catch (err) {
            const message = err.response?.data?.message || 'Unable to save popup announcement.';
            setError(message);
            notifyError(message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this popup announcement?')) return;
        try {
            await adminPopupAnnouncementService.delete(id);
            notifySuccess('Popup announcement deleted.');
            await fetchAnnouncements();
        } catch (err) {
            notifyError('Unable to delete popup announcement.');
        }
    };

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-4xl font-bold text-gray-800">Popup Announcements</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-white transition hover:bg-emerald-700"
                >
                    <FaPlus /> Add Popup
                </button>
            </div>

            {showForm && (
                <div className="mb-8 rounded-lg bg-white p-8 shadow-lg">
                    <h2 className="mb-6 text-2xl font-bold text-gray-800">{editingId ? 'Edit Popup' : 'New Popup'}</h2>
                    {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{error}</div>}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Title</label>
                            <input name="title" value={formData.title} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Type</label>
                            <select name="type" value={formData.type} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2">
                                <option value="news">News</option>
                                <option value="project">Project</option>
                                <option value="donation_campaign">Donation Campaign</option>
                                <option value="announcement">Announcement</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Status</label>
                            <select name="status" value={formData.status} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2">
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Priority</label>
                            <input type="number" min="0" name="priority" value={formData.priority} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Start At</label>
                            <input type="datetime-local" name="start_at" value={formData.start_at} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">End At</label>
                            <input type="datetime-local" name="end_at" value={formData.end_at} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Popup Image</label>
                            <input type="file" name="image" accept="image/*" onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Rating (1-5)</label>
                            <input type="number" min="1" max="5" name="rating" value={formData.rating} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        {formData.type === 'donation_campaign' && (
                            <>
                                <div>
                                    <label className="mb-2 block font-semibold text-gray-700">Donation Goal</label>
                                    <input type="number" min="0" name="donation_goal" value={formData.donation_goal} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                                </div>
                                <div>
                                    <label className="mb-2 block font-semibold text-gray-700">Donation Raised</label>
                                    <input type="number" min="0" name="donation_raised" value={formData.donation_raised} onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className="mb-2 block font-semibold text-gray-700">Message</label>
                            <textarea name="message" rows="4" value={formData.message} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <label className="md:col-span-2 flex items-center gap-2">
                            <input type="checkbox" name="show_once" checked={formData.show_once} onChange={handleChange} />
                            <span className="font-semibold text-gray-700">Show once per user</span>
                        </label>
                        <div className="md:col-span-2 flex gap-4">
                            <button type="submit" className="rounded-lg bg-emerald-600 px-6 py-2 text-white hover:bg-emerald-700">Save</button>
                            <button type="button" onClick={resetForm} className="rounded-lg bg-gray-400 px-6 py-2 text-white hover:bg-gray-500">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg bg-white shadow-lg">
                    <table className="w-full">
                        <thead className="bg-emerald-600 text-white">
                            <tr>
                                <th className="px-6 py-3 text-left">Title</th>
                                <th className="px-6 py-3 text-left">Type</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">Image</th>
                                <th className="px-6 py-3 text-left">Page</th>
                                <th className="px-6 py-3 text-left">Priority</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {announcements.map((item) => (
                                <tr key={item.id} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-4">{item.title}</td>
                                    <td className="px-6 py-4">{item.type}</td>
                                    <td className="px-6 py-4">{item.status}</td>
                                    <td className="px-6 py-4">
                                        {item.image ? (
                                            <img src={resolveMediaUrl(item.image)} alt={item.title} className="h-10 w-10 rounded object-cover" />
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {item.slug ? (
                                            <a
                                                href={`${window.location.origin.replace(':3001', ':3000')}/announcements/${item.slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-emerald-600 hover:underline"
                                            >
                                                Open Page
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">{item.priority}</td>
                                    <td className="flex gap-3 px-6 py-4">
                                        <button onClick={() => handleEdit(item)} className="text-emerald-600 hover:text-emerald-800"><FaEdit /></button>
                                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PopupAnnouncements;
